// ════════════════════════════════════════════════════════════
// / (accueil) — ECR
// ════════════════════════════════════════════════════════════
// Dashboard home mobile-first. Une carte par module (Dépenses,
// Encaissements) avec les compteurs principaux + raccourci action.
// ════════════════════════════════════════════════════════════
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  Banknote,
  Landmark,
  Receipt,
  Plus,
  AlertTriangle,
  ShieldCheck,
  type LucideIcon,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { canAccessRoute } from '@/types/auth';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { useDepenseCounts, useDepenses } from '@/queries/depenses';
import { usePaiementCounts, usePaiementsADeposer } from '@/queries/paiements';
import { canCreateDepense, SEUIL_DOUBLE_VALIDATION_MRU } from '@ecr/core/depenses';
import { canCreatePaiement } from '@ecr/core/paiements';
import { useSoldesTresorerie } from '@/queries/tresorerie';
import { formatMRU } from '@ecr/core/utils';
import { CompteLogo } from '@/components/tresorerie/CompteLogo';
import { Logo } from '@/components/Logo';

export const Route = createFileRoute('/_authed/')({
  component: HomePage,
});

type RoutePath = '/paiements' | '/paiements/new' | '/depenses' | '/depenses/new';

interface ModuleCardProps {
  to: RoutePath;
  icon: LucideIcon;
  title: string;
  subtitle: string;
  primary: { label: string; value: number };
  secondary?: { label: string; value: number };
  ctaTo?: RoutePath;
  ctaLabel?: string;
  accentClass: string;
  iconBgClass: string;
  gradientClass: string;
  stripClass: string;
  ctaButtonClass?: string;
}

function ModuleCard(props: ModuleCardProps) {
  const Icon = props.icon;
  return (
    <Card
      className={cn(
        'group relative overflow-hidden border-2 transition-all duration-200',
        'hover:-translate-y-1 hover:shadow-xl',
        props.accentClass,
      )}
    >
      {/* Strip coloré en haut */}
      <div className={cn('absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r', props.stripClass)} />
      {/* Gradient subtil de fond */}
      <div
        className={cn(
          'pointer-events-none absolute inset-0 bg-gradient-to-br opacity-60 transition-opacity group-hover:opacity-80',
          props.gradientClass,
        )}
      />
      {/* Icône décorative XL en filigrane */}
      <div className="pointer-events-none absolute -bottom-6 -right-6 opacity-[0.06] transition-opacity group-hover:opacity-10">
        <Icon className="size-32" strokeWidth={1.5} />
      </div>
      <CardContent className="relative space-y-3 p-4">
        <div className="flex items-start justify-between gap-3">
          <Link to={props.to} className="flex flex-1 items-center gap-3">
            <div
              className={cn(
                'flex size-12 shrink-0 items-center justify-center rounded-xl shadow-sm transition-transform group-hover:scale-110',
                props.iconBgClass,
              )}
            >
              <Icon className="size-6" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-foreground text-base font-bold leading-tight tracking-tight">
                {props.title}
              </p>
              <p className="text-muted-foreground truncate text-xs">{props.subtitle}</p>
            </div>
          </Link>
        </div>

        {/* Grille chiffres : 2 colonnes égales, hauteur fixe, baselines alignées */}
        <div className="grid min-h-[72px] grid-cols-2 items-end gap-3">
          <div className="flex flex-col">
            <p className="text-foreground font-mono text-3xl font-bold tabular-nums leading-none">
              {props.primary.value}
            </p>
            <p className="text-muted-foreground mt-1.5 text-[10px] font-semibold uppercase tracking-wider">
              {props.primary.label}
            </p>
          </div>
          {props.secondary && (
            <div className="border-border/60 flex flex-col border-l pl-3">
              <p className="text-foreground font-mono text-3xl font-bold tabular-nums leading-none">
                {props.secondary.value}
              </p>
              <p className="text-muted-foreground mt-1.5 text-[10px] font-semibold uppercase tracking-wider">
                {props.secondary.label}
              </p>
            </div>
          )}
        </div>

        {props.ctaTo && props.ctaLabel && (
          <Link to={props.ctaTo} className="block">
            <Button
              size="sm"
              className={cn(
                'w-full gap-2 font-medium shadow-sm transition-all hover:shadow group-hover:scale-[1.02]',
                props.ctaButtonClass ?? 'bg-foreground text-background hover:bg-foreground/90',
              )}
            >
              <Plus className="size-4" />
              {props.ctaLabel}
            </Button>
          </Link>
        )}
      </CardContent>
    </Card>
  );
}

function greetingTimeOfDay(): string {
  const h = new Date().getHours();
  if (h < 5) return 'Bonne nuit';
  if (h < 12) return 'Bonjour';
  if (h < 18) return 'Bon après-midi';
  return 'Bonsoir';
}

function HomePage() {
  const { profile, session } = useAuth();
  const role = profile?.role ?? null;
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const greeting = profile?.full_name || session?.user.email?.split('@')[0] || 'utilisateur';

  const { data: paiementCounts } = usePaiementCounts();
  const { data: paiementsADeposer = [] } = usePaiementsADeposer();

  const { data: depenseCounts } = useDepenseCounts();
  const { data: soldes = [] } = useSoldesTresorerie();
  const soldeTotal = soldes.reduce((acc, s) => acc + s.solde, 0);

  const paiementsEnAttente = paiementCounts?.en_attente ?? 0;
  const paiementsConfirmes = paiementCounts?.confirme ?? 0;

  const depensesDemandees = depenseCounts?.demandée ?? 0;
  const depensesPayees = depenseCounts?.payée ?? 0;

  const totalADeposer = paiementsADeposer.length;

  // Double validation « 4 yeux » : dépenses en attente que CE DAF doit valider
  // (créées par l'autre DAF). Requête limitée au rôle daf.
  const isDaf = role === 'daf';
  const userEmail = session?.user.email?.toLowerCase() ?? '';
  const { data: enAttenteValidation = [] } = useDepenses({
    status: ['demandée'],
    approvalStatus: ['pending'],
    limit: 100,
    enabled: isDaf,
  });
  const aValider = enAttenteValidation.filter((d) => d.createdByEmail.toLowerCase() !== userEmail);

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      {/* Salutation + bandeau accueil — bleu marine ECR */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0B1F3D] via-[#14396B] to-[#2461AE] p-5 text-white shadow-lg sm:p-6">
        {/* Halos décoratifs */}
        <div className="absolute -right-12 -top-12 size-40 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute -bottom-8 -left-8 size-32 rounded-full bg-sky-300/20 blur-2xl" />
        <div className="relative flex items-start gap-3">
          {/* Logo ECR dans badge blanc */}
          <div className="flex size-14 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white shadow-md ring-2 ring-white/30 sm:size-16">
            <Logo variant="icon" size="lg" />
          </div>
          <div className="min-w-0 flex-1 space-y-1">
            <p className="text-sm font-medium text-white/80">{greetingTimeOfDay()} 👋</p>
            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">{greeting}</h1>
            <p className="mt-1 text-sm text-white/80">Voici un aperçu de ton activité du jour.</p>
          </div>
        </div>
        {/* Mini-stats inline en bas du bandeau */}
        <div className="relative mt-4 flex flex-wrap gap-2">
          {totalADeposer > 0 && (
            <Link
              to="/paiements"
              className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-medium backdrop-blur-sm transition-colors hover:bg-white/25"
            >
              <AlertTriangle className="size-3.5" />
              {totalADeposer} à déposer
            </Link>
          )}
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-medium backdrop-blur-sm">
            <Banknote className="size-3.5" />
            {paiementsEnAttente + paiementsConfirmes} encaissements
          </span>
        </div>
      </div>

      {/* Alerte double validation : dépenses > 100 000 MRU / SWIFT à valider par ce DAF */}
      {isDaf && aValider.length > 0 && (
        <Card className="border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/30">
          <CardContent className="flex items-center gap-3 py-3">
            <ShieldCheck className="size-5 shrink-0 text-amber-600 dark:text-amber-400" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-900 dark:text-amber-100">
                {aValider.length} dépense{aValider.length > 1 ? 's' : ''} à valider
              </p>
              <p className="text-xs text-amber-700 dark:text-amber-300">
                Règle des 4 yeux ({'>'} {formatMRU(SEUIL_DOUBLE_VALIDATION_MRU)} ou SWIFT) :
                demandée{aValider.length > 1 ? 's' : ''} par l&apos;autre DAF, à valider avant
                paiement.
              </p>
            </div>
            <Link to="/depenses">
              <Button
                size="sm"
                variant="outline"
                className="border-amber-600 text-amber-700 dark:text-amber-300"
              >
                Voir
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {/* Alerte chèques à déposer */}
      {totalADeposer > 0 && canAccessRoute(role, '/paiements') && (
        <Card className="border-orange-300 bg-orange-50 dark:border-orange-700 dark:bg-orange-950/30">
          <CardContent className="flex items-center gap-3 py-3">
            <AlertTriangle className="size-5 shrink-0 text-orange-600 dark:text-orange-400" />
            <div className="flex-1">
              <p className="text-sm font-medium text-orange-900 dark:text-orange-100">
                {totalADeposer} paiement{totalADeposer > 1 ? 's' : ''} à déposer en banque
              </p>
              <p className="text-xs text-orange-700 dark:text-orange-300">
                Dépose + upload bordereau pour libérer l&apos;encaissement.
              </p>
            </div>
            <Link to="/paiements">
              <Button
                size="sm"
                variant="outline"
                className="border-orange-600 text-orange-700 dark:text-orange-300"
              >
                Voir
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {/* Modules — filtrés par accès rôle */}
      <div className="grid gap-3 sm:grid-cols-2">
        {canAccessRoute(role, '/paiements') && (
          <ModuleCard
            to="/paiements"
            icon={Banknote}
            title="Encaissements"
            subtitle="Cash, chèques, wallets"
            primary={{ label: 'En attente', value: paiementsEnAttente }}
            secondary={{ label: 'Compta.', value: paiementsConfirmes }}
            ctaTo={canCreatePaiement(role) ? '/paiements/new' : undefined}
            ctaLabel={canCreatePaiement(role) ? 'Nouvel encaissement' : undefined}
            accentClass="border-emerald-300 dark:border-emerald-800"
            stripClass="from-emerald-400 via-emerald-500 to-teal-500"
            gradientClass="from-emerald-100/80 via-emerald-50/40 to-transparent dark:from-emerald-950/50 dark:via-emerald-900/20"
            iconBgClass="bg-gradient-to-br from-emerald-500 to-teal-600 text-white"
            ctaButtonClass="bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:from-emerald-700 hover:to-teal-700"
          />
        )}
        {canAccessRoute(role, '/depenses') && (
          <ModuleCard
            to="/depenses"
            icon={Receipt}
            title="Dépenses"
            subtitle="Factures fournisseurs"
            primary={{ label: 'Demandées', value: depensesDemandees }}
            secondary={{ label: 'Compta.', value: depensesPayees }}
            ctaTo={canCreateDepense(role) ? '/depenses/new' : undefined}
            ctaLabel={canCreateDepense(role) ? 'Nouvelle dépense' : undefined}
            accentClass="border-blue-300 dark:border-blue-800"
            stripClass="from-blue-500 via-blue-600 to-indigo-600"
            gradientClass="from-blue-100/80 via-blue-50/40 to-transparent dark:from-blue-950/50 dark:via-blue-900/20"
            iconBgClass="bg-gradient-to-br from-blue-600 to-indigo-700 text-white"
            ctaButtonClass="bg-gradient-to-r from-blue-700 to-indigo-700 text-white hover:from-blue-800 hover:to-indigo-800"
          />
        )}
      </div>

      {/* Trésorerie — soldes des comptes */}
      {canAccessRoute(role, '/tresorerie') && soldes.length > 0 && (
        <Link to="/tresorerie" className="block">
          <Card className="transition-all hover:-translate-y-0.5 hover:shadow-lg">
            <CardContent className="space-y-3 p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#14396B] to-[#2461AE] text-white shadow-sm">
                    <Landmark className="size-5" />
                  </div>
                  <div>
                    <p className="text-foreground text-base font-bold leading-tight">Trésorerie</p>
                    <p className="text-muted-foreground text-xs">Soldes des comptes</p>
                  </div>
                </div>
                <p className="text-foreground font-mono text-lg font-bold tabular-nums">
                  {formatMRU(soldeTotal)}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4">
                {soldes.map((s) => (
                  <div key={s.id} className="flex items-center gap-2">
                    <CompteLogo logoKey={s.logoKey} type={s.type} className="size-9" />
                    <div className="min-w-0">
                      <p className="text-muted-foreground truncate text-[10px] font-semibold uppercase tracking-wider">
                        {s.label}
                      </p>
                      <p
                        className={cn(
                          'font-mono text-sm font-semibold tabular-nums',
                          s.solde < 0 ? 'text-red-600 dark:text-red-400' : 'text-foreground',
                        )}
                      >
                        {formatMRU(s.solde)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </Link>
      )}
    </div>
  );
}
